/**
 * Created by neo on 09.12.2024
 * Individual Challenge - Similar to RouteChallenge but without teams
 */
import { makeAutoObservable } from 'mobx';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { RouteChallengeLocation, createRouteChallengeLocation } from '../RouteChallenge/RouteChallengeLocation';
import { LocalizedEntity, createLocalizedEntity } from '../../LocalizedEntity';
import { Pageable } from '../../Interfaces/Pageable';
import { Media, createMedia } from '../../Media/Media';
import { MapRegion, createMapRegion } from '../RouteChallenge/MapRegion';
import { RouteChallengeWalkthroughEntry, createRouteChallengeWalkthroughEntry } from '../RouteChallenge/RouteChallengeWalkthroughEntry';
import dayjs from 'dayjs';

export type IndividualChallenge = LocalizedEntity & {
  challengeType: 'individual';
  templateId?: string;
  gymId?: string;
  totalCollectedPoints: number;
  totalParticipants: number;
  startDateTime: Date;
  endDateTime: Date;
  displayStartDateTime: Date;
  displayEndDateTime: Date;
  locations: RouteChallengeLocation[];
  started: boolean;
  ended: boolean;
  images: Media[];
  icon?: Media;
  mobilePreview?: Media;
  mapCenter: MapRegion;
  difficultyFactor: number;
  dailyPointsLimit?: number;
  maxParticipants?: number;
  beforeStartWalkthroughEntries: RouteChallengeWalkthroughEntry[];
  afterEndWalkthroughEntries: RouteChallengeWalkthroughEntry[];
  readonly totalPossiblePoints: number;
  readonly totalDurationInWeeks: number;
  readonly totalDuration: { weeks: number; days: number };
  readonly daysRemaining: number;
  readonly currentDay: number;
  readonly totalDays: number;
  readonly isActive: boolean;
  readonly status: 'pending' | 'running' | 'ended';
  getImage(lang: string): Media | undefined;
};

export function createIndividualChallenge(json?: Partial<IndividualChallenge>): IndividualChallenge {
  const base = createLocalizedEntity(json as any);
  return makeAutoObservable({
    ...base,
    challengeType: 'individual' as const,
    templateId: json?.templateId,
    gymId: json?.gymId ?? undefined,
    totalCollectedPoints: json?.totalCollectedPoints ?? 0,
    totalParticipants: json?.totalParticipants ?? 0,
    startDateTime: json?.startDateTime ? new Date(json.startDateTime as any) : new Date(),
    endDateTime: json?.endDateTime ? new Date(json.endDateTime as any) : new Date(),
    displayStartDateTime: json?.displayStartDateTime ? new Date(json.displayStartDateTime as any) : new Date(),
    displayEndDateTime: json?.displayEndDateTime ? new Date(json.displayEndDateTime as any) : new Date(),
    locations: ((json?.locations as any) ?? []).map((js: any) => createRouteChallengeLocation(js)),
    started: json?.started ?? false,
    ended: json?.ended ?? false,
    images: ((json?.images as any) ?? []).map((img: any) => createMedia(img)),
    icon: json?.icon ? createMedia(json.icon as any) : undefined,
    mobilePreview: json?.mobilePreview ? createMedia(json.mobilePreview as any) : undefined,
    mapCenter: createMapRegion(json?.mapCenter as any),
    difficultyFactor: json?.difficultyFactor ?? 0.5,
    dailyPointsLimit: json?.dailyPointsLimit,
    maxParticipants: json?.maxParticipants,
    beforeStartWalkthroughEntries: ((json?.beforeStartWalkthroughEntries as any) ?? []).map((entry: any) =>
      createRouteChallengeWalkthroughEntry(entry),
    ),
    afterEndWalkthroughEntries: ((json?.afterEndWalkthroughEntries as any) ?? []).map((entry: any) =>
      createRouteChallengeWalkthroughEntry(entry),
    ),

    get totalPossiblePoints(): number {
      return this.locations.reduce((acc, location) => acc + location.pointsRequired, 0);
    },

    get totalDurationInWeeks(): number {
      return dayjs(this.endDateTime).diff(dayjs(this.startDateTime), 'week');
    },

    get totalDuration(): { weeks: number; days: number } {
      const totalDays = dayjs(this.endDateTime).diff(dayjs(this.startDateTime), 'day');
      const weeks = Math.floor(totalDays / 7);
      const days = totalDays % 7;
      return { weeks, days };
    },

    get daysRemaining(): number {
      return Math.max(0, dayjs(this.endDateTime).diff(dayjs(), 'day'));
    },

    get currentDay(): number {
      if (!this.started) return 0;
      return dayjs().diff(dayjs(this.startDateTime), 'day') + 1;
    },

    get totalDays(): number {
      return dayjs(this.endDateTime).diff(dayjs(this.startDateTime), 'day');
    },

    get isActive(): boolean {
      return this.started && !this.ended;
    },

    get status(): 'pending' | 'running' | 'ended' {
      if (this.ended) return 'ended';
      if (this.started) return 'running';
      return 'pending';
    },

    getImage(lang: string): Media | undefined {
      const languageMedia = this.images.find((a) => a.language?.toLowerCase() === lang.toLowerCase());
      const germanMedia = this.images.find((a) => a.language?.toLowerCase() === 'de');
      return languageMedia ?? germanMedia ?? this.images[0];
    },
  });
}

export const IndividualChallengeApi = {
  find(request?: Partial<Pageable>): Promise<IndividualChallenge[]> {
    return HttpBackend.get(`/coach-program/engagement/individual-challenges`, request).then((json) =>
      json.map((entry: any) => createIndividualChallenge(entry)),
    );
  },

  myChallenges(
    request?: Partial<Pageable> & { startDateTime?: string; endDateTime?: string },
  ): Promise<IndividualChallenge[]> {
    return HttpBackend.get(`/coach-program/engagement/individual-challenges/my-challenges`, request).then((json) =>
      json.map((entry: any) => createIndividualChallenge(entry)),
    );
  },

  current(): Promise<IndividualChallenge[]> {
    return HttpBackend.get(`/coach-program/engagement/individual-challenges/current`).then((json) =>
      json.map((entry: any) => createIndividualChallenge(entry)),
    );
  },

  count(): Promise<number> {
    return HttpBackend.get(`/coach-program/engagement/individual-challenges/count`).then((res) => res ?? 0);
  },

  findOne(id: string): Promise<IndividualChallenge | undefined> {
    return HttpBackend.get(`/coach-program/engagement/individual-challenges/${id}`).then((json) =>
      json ? createIndividualChallenge(json) : undefined,
    );
  },
};
